/*
Inline Price Edit Component
Allows editing vehicle prices directly in the publish table with automatic platform sync

Features:
- Click-to-edit interface with hover indicators
- Real-time validation using existing validation rules
- Automatic sync to external platforms (MercadoLibre, ChileAutos, Yapo)
- Loading states, error handling, and success feedback
- Keyboard shortcuts (Enter to save, Escape to cancel)
- Leverages existing /api/vehicle_edit infrastructure

Usage:
<InlinePriceEdit
  vehicleId={vehicle._id}
  currentPrice={vehicle.price}
  onSave={handleInlinePriceSave}
  disabled={false}
/>

Autor: Assistant
Fecha: 2025-01-27
*/

import React, { useState, useRef, useEffect } from 'react'
import { Spinner, Text } from '@radix-ui/themes'
import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons'

import { useFormatter } from '@/utils/formats'
import { formatWithThousandSep } from '@/utils/utils'
import { Rule, validateFields } from '@/utils/validateForm'

interface InlinePriceEditProps {
  vehicleId: string
  currentPrice: number
  onSave: (
    vehicleId: string,
    newPrice: number
  ) => Promise<{ success: boolean; message?: string; syncInfo?: { platforms: string[] } }>
  disabled?: boolean
  className?: string
}

interface EditState {
  isEditing: boolean
  tempPrice: string
  isSaving: boolean
  error: string | null
  showSuccess: boolean
}

const InlinePriceEdit: React.FC<InlinePriceEditProps> = ({
  vehicleId,
  currentPrice,
  onSave,
  disabled = false,
  className = '',
}) => {
  const { formatCurrency } = useFormatter()
  const inputRef = useRef<HTMLInputElement>(null)

  const [state, setState] = useState<EditState>({
    isEditing: false,
    tempPrice: '',
    isSaving: false,
    error: null,
    showSuccess: false,
  })

  // Reset success state after a delay
  useEffect(() => {
    if (state.showSuccess) {
      const timer = setTimeout(() => {
        setState((prev) => ({ ...prev, showSuccess: false }))
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [state.showSuccess])

  // Focus input when entering edit mode
  useEffect(() => {
    if (state.isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [state.isEditing])

  const handleEdit = () => {
    if (disabled || state.isSaving) return

    setState((prev) => ({
      ...prev,
      isEditing: true,
      tempPrice: formatWithThousandSep(currentPrice),
      error: null,
      showSuccess: false,
    }))
  }

  const handleCancel = () => {
    setState((prev) => ({
      ...prev,
      isEditing: false,
      tempPrice: '',
      error: null,
    }))
  }

  const validatePrice = (priceString: string): { isValid: boolean; error?: string; numericValue?: number } => {
    if (!priceString.trim()) {
      return { isValid: false, error: 'El precio es requerido' }
    }

    // Remove formatting and convert to number
    const numericPrice = parseFloat(priceString.replace(/[^\d]/g, ''))

    if (isNaN(numericPrice) || numericPrice <= 0) {
      return { isValid: false, error: 'El precio debe ser mayor que cero' }
    }

    // Use existing validation rules
    const validationResult = validateFields([{ name: 'price', value: numericPrice, rule: Rule.GREATER_ZERO }])

    if (Object.keys(validationResult).length > 0) {
      return { isValid: false, error: validationResult.price || 'Precio inválido' }
    }

    return { isValid: true, numericValue: numericPrice }
  }

  const handleSave = async () => {
    const validation = validatePrice(state.tempPrice)

    if (!validation.isValid) {
      setState((prev) => ({ ...prev, error: validation.error || 'Error de validación' }))

      return
    }

    const numericPrice = validation.numericValue!

    // Check if price actually changed
    if (numericPrice === currentPrice) {
      handleCancel()

      return
    }

    setState((prev) => ({ ...prev, isSaving: true, error: null }))

    try {
      const result = await onSave(vehicleId, numericPrice)

      if (result.success) {
        setState((prev) => ({
          ...prev,
          isEditing: false,
          tempPrice: '',
          isSaving: false,
          showSuccess: true,
          error: null,
        }))
      } else {
        setState((prev) => ({
          ...prev,
          isSaving: false,
          error: result.message || 'Error al guardar el precio',
        }))
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isSaving: false,
        error: error instanceof Error ? error.message : 'Error desconocido',
      }))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, tempPrice: e.target.value, error: null }))
  }

  // Saving state
  if (state.isSaving) {
    return (
      <div className={`flex items-center justify-end gap-2 ${className}`}>
        <Spinner size="1" />
        <Text size="2" color="gray">
          Guardando...
        </Text>
      </div>
    )
  }

  // Success state
  if (state.showSuccess) {
    return (
      <div className={`flex items-center justify-end gap-2 ${className}`}>
        <CheckIcon className="w-4 h-4 text-green-600" />
        <Text size="2" color="green">
          Guardado
        </Text>
      </div>
    )
  }

  // Edit mode
  if (state.isEditing) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type="text"
            value={state.tempPrice}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            placeholder="Ingrese precio"
            className={`w-24 text-right text-sm border rounded p-1 ${state.error ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300'} focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none`}
          />
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleSave}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title="Guardar (Enter)"
            >
              <CheckIcon className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Cancelar (Esc)"
            >
              <Cross2Icon className="w-3 h-3" />
            </button>
          </div>
        </div>
        {state.error && (
          <Text size="1" color="red" className="absolute -bottom-5 right-0 whitespace-nowrap">
            {state.error}
          </Text>
        )}
      </div>
    )
  }

  // Display mode
  return (
    <div
      className={`group cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={handleEdit}
      title={disabled ? 'Edición deshabilitada' : 'Click para editar precio'}
    >
      <span
        className={`${!disabled ? 'group-hover:border group-hover:border-dashed group-hover:border-blue-300' : ''} px-1 rounded inline-block`}
      >
        {formatCurrency(currentPrice, '-')}
      </span>
    </div>
  )
}

export default InlinePriceEdit
